import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { type Container } from "@/builder/types";
import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { api, handleApiError } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { HttpError } from "@/utils/permissions";

import { type Checkin, type CheckinAction } from "../types";

export type CheckinList = Paginate<Checkin<true, true, true, true, true>>;
export type CheckinDetail = Checkin<true, true, true, true, true>;

export const checkins = createQueryKeys(
  "performance-objective-period-checkins",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    pending: (page?: number, filter?: SearchRequest) => [{ page, filter }],
    detail: (id: number, filter?: SearchRequest) => [{ id, filter }],
    form: (objectivePeriodId?: number, userId?: number) => [
      objectivePeriodId,
      userId,
    ],
    actions: (checkinId?: number, direction?: string) => [checkinId, direction],
  },
);

const include =
  "objectivePeriod,creatorUser,user,managerUser,participantUser,actions";

export const fetchCheckinsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...checkins.all(page, search),
    queryFn: () =>
      api
        .get<CheckinList>(`/performance/objective-period-checkins`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchCheckins = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCheckinsQueryOptions(page, search));

export const fetchPendingCheckinsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...checkins.pending(page, search),
    queryFn: () =>
      api
        .get<CheckinList>(`/performance/objective-period-checkins/pending`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPendingCheckins = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPendingCheckinsQueryOptions(page, search));

export const fetchCheckinQueryOptions = (id: number, search?: SearchRequest) =>
  queryOptions({
    ...checkins.detail(id, search),
    queryFn: async () => {
      try {
        const response = await api.get<CheckinDetail>(
          `/performance/objective-period-checkins/${id}`,
          {
            params: { include, ...search },
          },
        );
        return response.data;
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          throw new HttpError(404, "Not Found");
        }

        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status !== 404
        ) {
          handleApiError(error, false);
        }

        throw error;
      }
    },
    meta: { hideError: true },
  });

export const useFetchCheckin = (id?: number, search?: SearchRequest) =>
  useQuery({
    ...fetchCheckinQueryOptions(id ?? 0, search),
    enabled: id !== undefined,
  });

export type CreateCheckinRequest = {
  performance_objective_period_id: number;
  user_id: number;
  containers?: Container[];
  elements?: Array<{ id: number; value: any }>;
};

export const useCreateCheckin = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({ payload }: { payload: CreateCheckinRequest }) =>
      api
        .post(`/performance/objective-period-checkins`, payload)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checkins.all._def });
      queryClient.invalidateQueries({ queryKey: checkins.pending._def });
    },
  });
};

export type UpdateCheckinRequest = {
  status: Checkin["status"];
  reason?: string | null;
  containers?: Container[];
  elements?: Array<{ id: number; value: any }>;
};

export const useUpdateCheckin = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({
      checkinId,
      payload,
    }: {
      checkinId: number;
      payload: UpdateCheckinRequest;
    }) =>
      api
        .put(`/performance/objective-period-checkins/${checkinId}`, payload)
        .then((response) => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(checkins.detail(variables.checkinId));
      queryClient.invalidateQueries({ queryKey: checkins.all._def });
      queryClient.invalidateQueries({ queryKey: checkins.pending._def });
    },
  });
};

export type ApproveCheckinRequest = {
  elements: Array<{ id: number; value: any }>;
};

export const useApproveCheckin = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({
      checkinId,
      payload,
    }: {
      checkinId: number;
      payload: ApproveCheckinRequest;
    }) =>
      api
        .put(
          `/performance/objective-period-checkins/${checkinId}/approve`,
          payload,
        )
        .then((response) => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(checkins.detail(variables.checkinId));
      queryClient.invalidateQueries({ queryKey: checkins.all._def });
      queryClient.invalidateQueries({ queryKey: checkins.pending._def });
    },
  });
};

export type RejectCheckinRequest = {
  status: "rejected" | "pending_user_edit";
  reason?: string;
};

export const useRejectCheckin = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: ({
      checkinId,
      payload,
    }: {
      checkinId: number;
      payload: RejectCheckinRequest;
    }) =>
      api
        .put(
          `/performance/objective-period-checkins/${checkinId}/reject`,
          payload,
        )
        .then((response) => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(checkins.detail(variables.checkinId));
      queryClient.invalidateQueries({ queryKey: checkins.all._def });
      queryClient.invalidateQueries({ queryKey: checkins.pending._def });
    },
  });
};

export const useDeleteCheckin = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (checkinId: number) =>
      api
        .delete(`/performance/objective-period-checkins/${checkinId}`)
        .then((response) => response.data),
    onSuccess: (_, checkinId) => {
      queryClient.invalidateQueries({ queryKey: checkins.all._def });
      queryClient.invalidateQueries({ queryKey: checkins.pending._def });
      queryClient.invalidateQueries(checkins.detail(checkinId));
    },
  });
};

export type CheckinFormResponse = {
  containers: Container[];
};

export const fetchCheckinFormQueryOptions = (
  objectivePeriodId?: number,
  userId?: number,
) =>
  queryOptions({
    ...checkins.form(objectivePeriodId, userId),
    queryFn: async () => {
      try {
        const response = await api.get<CheckinFormResponse>(
          `/performance/objective-periods/${objectivePeriodId}/users/${userId}/objective-period-checkin-form`,
        );
        return response.data;
      } catch (error: unknown) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          throw new HttpError(404, "Not Found");
        }

        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status !== 404
        ) {
          handleApiError(error, false);
        }

        throw error;
      }
    },
    enabled: objectivePeriodId !== undefined && userId !== undefined,
    meta: { hideError: true },
  });

export const useFetchCheckinForm = (
  objectivePeriodId?: number,
  userId?: number,
) => useQuery(fetchCheckinFormQueryOptions(objectivePeriodId, userId));

export type CheckinActionsResponse = {
  data: CheckinAction[];
};

export const fetchCheckinActionsQueryOptions = (
  checkinId?: number,
  direction: "asc" | "desc" = "asc",
) =>
  queryOptions({
    ...checkins.actions(checkinId, direction),
    queryFn: () =>
      api
        .get<CheckinActionsResponse>(
          `/performance/objective-period-checkins/${checkinId}/actions`,
          {
            params: {
              include: "user",
              direction,
            },
          },
        )
        .then((response) => response.data),
    enabled: checkinId !== undefined,
  });

export const useFetchCheckinActions = (
  checkinId?: number,
  direction: "asc" | "desc" = "asc",
) => useQuery(fetchCheckinActionsQueryOptions(checkinId, direction));
